// Lesen und Schreiben des Ansichts-Zustands der Projektseite.
//
// WOFUER: dieser Block sass in `routes/Project.tsx` und hat die Datei ueber die
// 500-Zeilen-Grenze getrieben. Er ist reine Speicher-Logik ohne JSX und
// deshalb sauber herausloesbar. VERHALTEN UNVERAENDERT — dieselben Schluessel,
// dieselbe Kodierung, dieselben Vorgaben.
//
// Project.tsx benutzt Ansichts-Ids ("table", "kanban", …) und eine
// `tableFilter`-Zeichenkette, die zu keinem der getippten ViewState-Enums
// passen. Beide reisen in `ViewState.filters` als synthetische Regeln mit dem
// Feld "_view" / "_filter" mit: `sanitizeFilters` in viewStateStore prueft nur
// `typeof field === "string"`, die Werte ueberleben also den Hin- und Rueckweg.

import { loadGroupBy, type GroupBy } from "../../lib/grouping";
import { loadDefaultView } from "../../lib/userDefaults";
import { type TableColumnId } from "../../lib/columns";
import { loadViewState, saveViewState } from "../../lib/viewStateStore";
import { DEFAULT_VIEW_STATE, type FilterRule } from "../../lib/viewState";

/** Der Schluessel, unter dem eine Projektseite ihren Zustand ablegt. */
export function projectScopeKey(pid: number): string {
  return `project:${pid}`;
}

/** The synthetic meta fields Project.tsx smuggles through ViewState.filters. */
const META_FIELDS = ["_view", "_filter"] as const;

function isMetaRule(f: FilterRule): boolean {
  return (META_FIELDS as readonly string[]).includes(f.field as string);
}

/**
 * Embeds active view tab and tableFilter into a ViewState.filters array,
 * alongside the REAL filter rules (#872/#873).
 *
 * The two kinds coexist in one array on purpose: the engine's `matchesFilter`
 * ends in `default: return true`, so the synthetic `_view`/`_filter` rules are
 * pass-through no-ops for every view, while `sanitizeFilters` keeps them because
 * it only validates `typeof field === "string"`.
 */
export function encodeProjectMeta(
  view: string,
  tableFilter: string,
  realFilters: FilterRule[] = [],
): FilterRule[] {
  // Double-cast through unknown: "_view"/"_filter" are not FilterField literals
  // but survive sanitizeFilters because it only validates typeof === "string".
  return [
    { field: "_view" as unknown as FilterRule["field"], op: "is", value: view },
    { field: "_filter" as unknown as FilterRule["field"], op: "is", value: tableFilter },
    ...realFilters.filter((f) => !isMetaRule(f)),
  ];
}

/** Extracts the encoded view tab and tableFilter from a stored filters array. */
function decodeProjectMeta(filters: FilterRule[]): {
  view: string | undefined;
  tableFilter: string | undefined;
  realFilters: FilterRule[];
} {
  const fv = filters.find((f) => (f.field as string) === "_view");
  const ff = filters.find((f) => (f.field as string) === "_filter");
  return {
    view: typeof fv?.value === "string" ? fv.value : undefined,
    tableFilter: typeof ff?.value === "string" ? ff.value : undefined,
    // Everything that is not a meta rule is a real filter. A state stored
    // before #872 contains only meta rules, so this is [] for those — existing
    // persisted view states therefore load exactly as before.
    realFilters: filters.filter((f) => !isMetaRule(f)),
  };
}

/** Returns the ViewState defaults for a project — migration fallback from the
 *  old per-project groupBy store and the global default view setting. */
function makeProjectDefaults(pid: number) {
  return {
    ...DEFAULT_VIEW_STATE,
    groupBy: loadGroupBy(pid) as typeof DEFAULT_VIEW_STATE.groupBy,
    filters: encodeProjectMeta(loadDefaultView(), "all"),
    // Standardmaessig ausgeblendete Spalten der Projekttabelle. Ohne das waren
    // 12 Spalten sichtbar, deren feste Spuren + Luecken breiter sind als die
    // Inhaltsflaeche bei 1440 px — die Titelspalte fiel dadurch auf 0 px und
    // die Aufgabentitel waren UNSICHTBAR (Audit 2026-09-04). Wer das
    // Spalten-Menue schon benutzt hat, hat einen gespeicherten ViewState und
    // behaelt seine Wahl: coerceViewState() greift diese Vorgaben nur, wenn
    // `hiddenColumns` im gespeicherten Objekt fehlt (viewStateStore.ts:74).
    //
    // ACHTUNG, bewusst nur zwei statt drei: `"type"` ist KEIN `RecordColumnId`
    // (viewState.ts:72-80 kennt status/assignees/labels/start/end/priority/
    // project/ticket). `sanitizeColumns()` (viewStateStore.ts:55-60) wuerde
    // einen `"type"`-Eintrag beim naechsten Laden still verwerfen, die Spalte
    // waere wieder da und der Standard damit eine Luege. `"type"` laesst sich
    // erst ausblenden, wenn es in `RecordColumnId` + `COLUMN_IDS` aufgenommen
    // ist — das ist eine Aenderung am GETEILTEN Hub-Typ und gehoert nicht in
    // diesen Fix.
    hiddenColumns: ["ticket", "labels"] as typeof DEFAULT_VIEW_STATE.hiddenColumns,
  };
}

export interface ProjectViewState {
  view: string;
  groupBy: GroupBy;
  hiddenColumns: Set<TableColumnId>;
  tableFilter: string;
  filters: FilterRule[];
}

/** Reads and decodes the four per-project view-state fields from viewStateStore. */
export function loadProjectViewState(pid: number): ProjectViewState {
  const stored = loadViewState(projectScopeKey(pid), makeProjectDefaults(pid));
  const { view, tableFilter, realFilters } = decodeProjectMeta(stored.filters);
  return {
    view: view ?? loadDefaultView(),
    groupBy: stored.groupBy as GroupBy,
    hiddenColumns: new Set(stored.hiddenColumns as TableColumnId[]),
    tableFilter: tableFilter ?? "all",
    filters: realFilters,
  };
}

/** Writes the full four-field snapshot back under `project:<id>`. */
export function saveProjectViewState(
  pid: number,
  next: {
    view: string;
    groupBy: GroupBy;
    hiddenColumns: Set<TableColumnId>;
    tableFilter: string;
    filters: FilterRule[];
  },
): void {
  saveViewState(projectScopeKey(pid), {
    ...DEFAULT_VIEW_STATE,
    groupBy: next.groupBy as typeof DEFAULT_VIEW_STATE.groupBy,
    hiddenColumns: [...next.hiddenColumns] as typeof DEFAULT_VIEW_STATE.hiddenColumns,
    filters: encodeProjectMeta(next.view, next.tableFilter, next.filters),
  });
}
